import { useState, useEffect, useCallback, FunctionComponent } from 'react';
import { useHistory, useParams } from 'react-router';
import { IndividualProductBundle, IndividualProductBundleTranslations } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { BodyText, Breadcrumb, Div, Button, Title } from '@gaiads/telia-react-component-library';
import { BreadcrumbItemProps } from '@gaiads/telia-react-component-library/build/types/components/Breadcrumb/BreadcrumbItem/BreadcrumbItem';
import { useDispatch, useSelector } from 'react-redux';
import { LocalBundle, LocalBundleEnvelope } from '@cdaas/api-clients/dist/product-service-openapi';
import { UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { LocalBundleEnvelopeWithChosenMainVariant } from '../../../types';
import { modalClose, modalShow, shopSetProduct, shopClearOrderOnBehalfUser, shopClearOrderForOrganization } from '../../../store/actions';
import { RootState } from '../../../store/reducers';
import { devLogger, getBundleById, hasUserARequiredRole, isSelectedBundleAvailable, isSingleDevicePolicyInEffectForUser } from '../../../utils';
import i18next from 'i18next';

interface BundleState {
  bundle: LocalBundleEnvelope | null;
  loading: boolean;
  errorText: string;
}

const Bundle: FunctionComponent = () => {
  const [bundleState, setBundleState] = useState<BundleState>({
    bundle: null,
    loading: true,
    errorText: '',
  });

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemProps[]>([]);

  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const product = useSelector((state: RootState) => state.shop.cart);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const user = useSelector((state: RootState) => state.authentication.user);

  const { t, i18n } = useTranslation('bundle');
  const { t: tProduct } = useTranslation('product');
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const generateBreadcrumbs = useCallback((brandName: string, bundleName: string): void => {
    const shop = {
      label: 'Shop',
      to: '/shop',
      onClick: () => null,
      value: '',
    };
    const brand = {
      label: brandName,
      to: `/shop?brand=${brandName}`,
      onClick: () => null,
      value: '',
    };
    const currentItem = {
      label: bundleName,
      onClick: () => null,
      value: '',
    };
    setBreadcrumbs([shop, brand, currentItem]);
  }, []);

  const getBundle = useCallback(
    async (id: string): Promise<void> => {
      const bundle = await getBundleById(id);
      if (bundle) {
        setBundleState({ errorText: '', loading: false, bundle });
        generateBreadcrumbs(bundle.bundle.main_item.brand ?? '', bundle.bundle.name);
      } else {
        setBundleState({ errorText: t('getBundleErrorTitle'), loading: false, bundle });
      }
    },
    [generateBreadcrumbs, t],
  );

  useEffect(() => {
    getBundle(params.id ?? '0');
  }, [getBundle, params.id, i18n.language]);

  const showReplaceCartContentModal = (selectedBundle: LocalBundleEnvelopeWithChosenMainVariant): void => {
    dispatch(
      modalShow(
        t('bundleExistInCartModal.title'),
        t('bundleExistInCartModal.acceptButton'),
        t('bundleExistInCartModal.cancelButton'),
        () => {
          dispatch(shopSetProduct(selectedBundle));
          dispatch(modalClose());
          history.push('/shop/checkout');
        },
        () => dispatch(modalClose()),
        <BodyText>{t('bundleExistInCartModal.bodyText')}</BodyText>,
      ),
    );
  };

  const onBuyBundleClick = (_bundle: LocalBundle, chosenMainItemVariantId: number): void => {
    devLogger('buying bundle!', bundleState.bundle, chosenMainItemVariantId);
    const selectedBundle: LocalBundleEnvelopeWithChosenMainVariant = {
      ...bundleState.bundle!,
      chosenMainItemVariantId,
    };
    if (!isSelectedBundleAvailable(bundleState.bundle, chosenMainItemVariantId)) {
      dispatch(
        modalShow(
          t('bundleOutOfStockModal.title'),
          t('bundleOutOfStockModal.acceptButton'),
          t('bundleOutOfStockModal.cancelButton'),
          () => {
            dispatch(modalClose());
            if (product) {
              showReplaceCartContentModal(selectedBundle);
            } else {
              dispatch(shopSetProduct(selectedBundle));
              history.push('/shop/checkout');
            }
          },
          () => dispatch(modalClose()),
          <BodyText>{t('bundleOutOfStockModal.bodyText')}</BodyText>,
        ),
      );
    } else if (product) {
      showReplaceCartContentModal(selectedBundle);
    } else {
      dispatch(shopSetProduct(selectedBundle));
      history.push('/shop/checkout');
    }
  };

  const onOrderingOnBehalfCancelClick = () => {
    dispatch(shopClearOrderOnBehalfUser());
    history.push('/home');
  };

  const onOrderingForOrganizationCancelClick = () => {
    dispatch(shopClearOrderForOrganization());
    history.push('/home');
  };

  const getLocalizedTermsUrl = () => {
    if (i18next.language === 'no') {
      return process.env.REACT_APP_INSURANCE_URI_NO;
    }
    return process.env.REACT_APP_INSURANCE_URI;
  };

  const bundleTranslations: IndividualProductBundleTranslations = {
    availabilityAvailable: t('IndividualProductBundle.availabilityAvailable'),
    availabilityFewLeft: t('IndividualProductBundle.availabilityFewLeft'),
    availabilityNotAvailable: t('IndividualProductBundle.availabilityNotAvailable'),
    availabilityReadyToShip: t('IndividualProductBundle.availabilityReadyToShip'),
    availabilityNotReadyToShip: t('IndividualProductBundle.availabilityNotReadyToShip'),
    showFullDescription: t('IndividualProductBundle.showFullDescription'),
    comesBundledUp: t('IndividualProductBundle.comesBundledUp'),
    paymentSingle: t('IndividualProductBundle.paymentSingle'),
    paymentMulti: t('IndividualProductBundle.paymentMulti'),
    vat: t('IndividualProductBundle.vat'),
    includingVat: t('IndividualProductBundle.includingVat'),
    excludingVat: t('IndividualProductBundle.excludingVat'),
    totalMonthlyPrice: t('IndividualProductBundle.totalMonthlyPrice'),
    readyButton: t('IndividualProductBundle.readyButton'),
    secondaryButton: '',
    perMonth: t('IndividualProductBundle.perMonth'),
    canNotShowBundle: t('IndividualProductBundle.canNotShowBundle'),
    currency: user?.organization?.locale.currency.toLocaleUpperCase() ?? '',
    description: t('IndividualProductBundle.description'),
    dynamicPricingInsurance: {
      insured: t('IndividualProductBundle.dynamicPricingInsurance.insured'),
      shortExplanation: t('IndividualProductBundle.dynamicPricingInsurance.shortExplanation'),
      bulletPointFirstAccidents: t('IndividualProductBundle.dynamicPricingInsurance.bulletPointFirstAccidents'),
      bulletPointSecondTheft: t('IndividualProductBundle.dynamicPricingInsurance.bulletPointSecondTheft'),
      bulletPointThirdWater: t('IndividualProductBundle.dynamicPricingInsurance.bulletPointThirdWater'),
      terms: t('IndividualProductBundle.dynamicPricingInsurance.terms'),
      termsUrl: getLocalizedTermsUrl() || '',
    },
    singleDevicePolicyTitle: t('IndividualProductBundle.singleDevicePolicyTitle'),
    singleDevicePolicyText: t(
      orderingOnBehalfUserData.status ? 'IndividualProductBundle.singleDevicePolicyTextOnBehalf' : 'IndividualProductBundle.singleDevicePolicyText',
    ),
    yourAllowance: t(orderingOnBehalfUserData.status ? 'IndividualProductBundle.userAllowance' : 'IndividualProductBundle.yourAllowance'),
    yourAllowanceExplanation: t(
      orderingOnBehalfUserData.status ? 'IndividualProductBundle.userAllowanceExplanation' : 'IndividualProductBundle.yourAllowanceExplanation',
    ),
    salaryDeductionEstimate: t('IndividualProductBundle.salaryDeductionEstimate'),
    addonProductImageAltText: t('IndividualProductBundle.addonProductImageAltText'),
    totalPrice: t('IndividualProductBundle.totalPrice'),
    months: t('IndividualProductBundle.months'),
    monthlySalaryDeductionExplanation: t(
      orderingOnBehalfUserData.status
        ? 'IndividualProductBundle.monthlySalaryDeductionExplanationOob'
        : 'IndividualProductBundle.monthlySalaryDeductionExplanation',
    ),
    oneTimeSalaryDeductionExplanation: t(
      orderingOnBehalfUserData.status
        ? 'IndividualProductBundle.oneTimeSalaryDeductionExplanationOob'
        : 'IndividualProductBundle.oneTimeSalaryDeductionExplanation',
    ),
  };

  const allowance = orderingForOrganization
    ? undefined
    : orderingOnBehalfUserData.status
      ? orderingOnBehalfUserData.userToOrderFor?.allowance
      : user?.allowance;
  const allowanceType = orderingForOrganization
    ? undefined
    : orderingOnBehalfUserData.status
      ? orderingOnBehalfUserData.userToOrderFor?.allowance_type
      : user?.allowance_type;

  const renderOrderingForOrganization = () => (
    <>
      <Title inlineBlock size="xxs" color="gray800" padding={{ right: 'sm' }}>
        {tProduct('OrderingForOrganization.title')}:
      </Title>
      <BodyText inlineBlock padding={{ right: 'sm' }} color="gray400">
        {selectedOrganization?.name || ownOrganization?.name}
      </BodyText>
      <Button variant="text" inlineBlock size="lg" onClick={onOrderingForOrganizationCancelClick}>
        {tProduct('OrderingForOrganization.cancelButton')}
      </Button>
    </>
  );

  const renderOrderingOnBehalfUser = () => (
    <>
      <Title inlineBlock size="xxs" color="gray800" padding={{ right: 'sm' }}>
        {tProduct('OrderingOnBehalf.title')}:
      </Title>
      <BodyText inlineBlock padding={{ right: 'sm' }} color="gray400">
        {`${orderingOnBehalfUserData.userToOrderFor?.name || orderingOnBehalfUserData.userToOrderFor?.email}
          (${orderingOnBehalfUserData.userToOrderFor?.user_group?.name})`}
      </BodyText>
      <Button variant="text" inlineBlock size="lg" onClick={onOrderingOnBehalfCancelClick}>
        {tProduct('OrderingOnBehalf.cancelButton')}
      </Button>
    </>
  );

  const isSingleDevicePolicyInEffect = () =>
    orderingForOrganization
      ? false
      : orderingOnBehalfUserData.status
        ? isSingleDevicePolicyInEffectForUser('oob')
        : isSingleDevicePolicyInEffectForUser('authenticated');

  const isOnBehalfOrder = orderingForOrganization || orderingOnBehalfUserData.status;

  return (
    <>
      <Div borderRadius={{ size: 'sm' }} padding={{ horizontal: 'md', vertical: 'lg' }} boxShadow="sm" margin={{ top: 'lg' }} shown={isOnBehalfOrder}>
        {orderingForOrganization && renderOrderingForOrganization()}
        {orderingOnBehalfUserData.status && renderOrderingOnBehalfUser()}
      </Div>
      <Div margin={{ vertical: 'lg' }}>
        <Breadcrumb items={breadcrumbs} />
        <IndividualProductBundle
          localBundleEnvelope={bundleState.bundle}
          errorText={bundleState.errorText}
          loading={bundleState.loading}
          translations={bundleTranslations}
          onReadyButtonClick={onBuyBundleClick}
          disableBuy={
            isSingleDevicePolicyInEffect() ||
            !(hasUserARequiredRole([UserRole.User]) || (hasUserARequiredRole([UserRole.Orderer]) && isOnBehalfOrder))
          }
          singleDevicePolicyInEffect={isSingleDevicePolicyInEffect()}
          allowance={allowance}
          allowanceType={allowanceType}
          locale={user?.organization?.locale.language}
          procurementLimit={user?.policies?.effective_lease_procurement_limit ? user.policies.effective_lease_procurement_limit : undefined}
        />
      </Div>
    </>
  );
};

export default Bundle;
